import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { sendSuggestion } from '../../util/api';
import { emailFormatValid, required } from '../../util/validators';
import { getCurrentUserTypeRoles } from '../../util/userHelpers';

import {
  FieldTextInput,
  Form,
  Button,
  Modal,
  PrimaryButton,
  SecondaryButton,
} from '../../components';
import approvedPrimaryButtonCss from '../../styles/approvedPrimaryButton.module.css';

import css from './SuggestionsModal.module.css';

const SuggestionsModal = props => {
  const { currentUser, onManageDisableScrolling } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const [isOpen, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const isAuthenticated = !!currentUser?.id?.uuid;
  const roles = getCurrentUserTypeRoles(config, currentUser);
  const role = !isAuthenticated
    ? 'Visitante'
    : roles.provider
    ? 'Provider'
    : roles.customer
    ? 'Cliente'
    : 'Usuario';
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const initialValues = {
    name: currentUser?.attributes?.profile?.displayName || '',
    email: currentUser?.attributes?.email || '',
  };
  const close = () => {
    setOpen(false);
    setSent(false);
    setSubmitError(null);
  };
  const requiredMessage = intl.formatMessage({ id: 'SuggestionsModal.required' });

  return (
    <div className={css.root}>
      <Button
        className={classNames(css.openButton, approvedPrimaryButtonCss.approvedPrimaryButton)}
        type="button"
        onClick={() => setOpen(true)}
      >
        <FormattedMessage id="SuggestionsModal.open" />
      </Button>
      <Modal
        id="SuggestionsModal"
        containerClassName={css.modal}
        contentClassName={css.content}
        isOpen={isOpen}
        onClose={close}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        hideCloseButton
        closeButtonMessage={intl.formatMessage({ id: 'SuggestionsModal.cancel' })}
      >
        {sent ? (
          <div>
            <h2 className={css.title}>
              <FormattedMessage id="SuggestionsModal.sentTitle" />
            </h2>
            <p className={css.intro}>
              <FormattedMessage id="SuggestionsModal.sentMessage" />
            </p>
            <PrimaryButton type="button" onClick={close}>
              <FormattedMessage id="SuggestionsModal.close" />
            </PrimaryButton>
          </div>
        ) : (
          <>
            <h2 className={css.title}>
              <FormattedMessage id="SuggestionsModal.title" />
            </h2>
            <p className={css.intro}>
              <FormattedMessage id="SuggestionsModal.intro" />
            </p>
            <FinalForm
              initialValues={initialValues}
              onSubmit={values => {
                setSubmitError(null);
                return sendSuggestion({
                  ...values,
                  role,
                  pageUrl,
                })
                  .then(() => setSent(true))
                  .catch(error => {
                    setSubmitError(
                      error?.message || intl.formatMessage({ id: 'SuggestionsModal.error' })
                    );
                  });
              }}
              render={({ handleSubmit, submitting, invalid }) => (
                <Form className={css.form} onSubmit={handleSubmit}>
                  <FieldTextInput
                    name="subject"
                    id="suggestion-subject"
                    type="text"
                    label={intl.formatMessage({ id: 'SuggestionsModal.subject' })}
                    validate={required(requiredMessage)}
                  />
                  <FieldTextInput
                    name="message"
                    id="suggestion-message"
                    type="textarea"
                    label={intl.formatMessage({ id: 'SuggestionsModal.message' })}
                    validate={required(requiredMessage)}
                  />
                  <div className={css.identityFields}>
                    <FieldTextInput
                      name="name"
                      id="suggestion-name"
                      type="text"
                      label={intl.formatMessage({ id: 'SuggestionsModal.name' })}
                      disabled={isAuthenticated}
                    />
                    <FieldTextInput
                      name="email"
                      id="suggestion-email"
                      type="email"
                      label={intl.formatMessage({ id: 'SuggestionsModal.email' })}
                      disabled={isAuthenticated}
                      validate={value =>
                        value
                          ? emailFormatValid(
                              intl.formatMessage({ id: 'SuggestionsModal.invalidEmail' })
                            )(value)
                          : undefined
                      }
                    />
                  </div>
                  {submitError ? (
                    <p className={css.error} role="alert">
                      {submitError}
                    </p>
                  ) : null}
                  <div className={css.actions}>
                    <SecondaryButton type="button" onClick={close} disabled={submitting}>
                      <FormattedMessage id="SuggestionsModal.cancel" />
                    </SecondaryButton>
                    <PrimaryButton
                      className={approvedPrimaryButtonCss.approvedPrimaryButton}
                      type="submit"
                      inProgress={submitting}
                      disabled={invalid || submitting}
                    >
                      <FormattedMessage id="SuggestionsModal.submit" />
                    </PrimaryButton>
                  </div>
                </Form>
              )}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default SuggestionsModal;
